import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompareEntities } from '../api/intelligence';

const TYPE_BADGES: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
};

export default function ComparePage() {
  const navigate = useNavigate();
  const [ids, setIds] = useState<string[]>(['', '']);
  const [submitted, setSubmitted] = useState<string[]>([]);
  const { data, isLoading } = useCompareEntities(submitted);

  const handleAdd = () => setIds([...ids, '']);
  const handleRemove = (idx: number) => setIds(ids.filter((_, i) => i !== idx));
  const handleChange = (idx: number, val: string) => {
    const next = [...ids];
    next[idx] = val;
    setIds(next);
  };
  const handleCompare = () => {
    const valid = ids.filter((id) => id.trim().length > 0);
    if (valid.length >= 2) setSubmitted(valid);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Comparaison d'entites</h1>
        <p className="text-sm text-gray-500 mt-1">Comparez les proprietes, connexions et risques entre entites</p>
      </div>

      {/* Input */}
      <div className="glass-panel p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-200">Entites a comparer</h3>
        {ids.map((id, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              value={id}
              onChange={(e) => handleChange(idx, e.target.value)}
              className="flex-1 px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300 font-mono"
              placeholder="UUID de l'entite..."
            />
            {ids.length > 2 && (
              <button onClick={() => handleRemove(idx)} className="px-2 py-2 text-red-400 hover:text-red-300">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={handleAdd} className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 bg-surface-tertiary rounded-lg">
            + Ajouter
          </button>
          <button
            onClick={handleCompare}
            disabled={ids.filter((i) => i.trim()).length < 2}
            className="px-4 py-1.5 text-xs font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 disabled:opacity-50 transition-colors"
          >
            Comparer
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="space-y-6">
          {/* Similarity score */}
          <div className="glass-panel p-5 text-center">
            <p className="text-xs text-gray-500 mb-2">Score de similarite</p>
            <p className={`text-4xl font-bold ${data.similarity_score >= 0.7 ? 'text-green-400' : data.similarity_score >= 0.4 ? 'text-amber-400' : 'text-red-400'}`}>
              {Math.round(data.similarity_score * 100)}%
            </p>
          </div>

          {/* Entity cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.entities.map((e) => (
              <div
                key={e.id}
                className="glass-panel p-4 cursor-pointer hover:border-cyber-cyan/30 transition-colors"
                onClick={() => navigate(`/entities/${e.id}`)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold ${TYPE_BADGES[e.entity_type] || 'bg-gray-500/20 text-gray-400'}`}>
                    {e.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-200">{e.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{e.entity_type.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Risque</span>
                    <span className={`font-mono ${(e.risk_score ?? 0) >= 0.7 ? 'text-red-400' : (e.risk_score ?? 0) >= 0.4 ? 'text-amber-400' : 'text-green-400'}`}>
                      {Math.round((e.risk_score ?? 0) * 100)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Relations</span>
                    <span className="text-gray-300">{e.relationship_count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Common connections */}
          {data.common_connections.length > 0 && (
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-gray-200 mb-3">
                Connexions communes ({data.common_connections.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.common_connections.map((c, i) => (
                  <div key={i} className="px-3 py-2 bg-surface-tertiary/50 rounded-lg text-xs text-gray-300">
                    {JSON.stringify(c)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shared properties */}
          {data.shared_properties.length > 0 && (
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-gray-200 mb-3">Proprietes partagees</h3>
              <div className="space-y-2">
                {data.shared_properties.map((sp, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-surface-tertiary/50 rounded-lg text-xs">
                    <span className="text-gray-400">{sp.key}</span>
                    <span className="text-gray-300">{String(sp.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Differences */}
          {data.differences.length > 0 && (
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-gray-200 mb-3">Differences</h3>
              <div className="space-y-2">
                {data.differences.map((d, i) => (
                  <div key={i} className="px-3 py-2 bg-surface-tertiary/50 rounded-lg text-xs">
                    <span className="text-gray-400 font-medium">{d.key}: </span>
                    {Object.entries(d.values).map(([eid, val]) => (
                      <span key={eid} className="text-gray-300 mr-3">
                        <span className="text-gray-600">{eid.substring(0, 6)}:</span> {String(val)}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
